/* eslint-disable no-unused-vars */
import type { ValidationError } from "@types";
import { useEffect, useRef } from "react";
import Router from "next/router";
import { separateMoneyNumbers, toFarsiNumber } from "./digits";

export function isServer() {
  return typeof window === "undefined";
}

export function addFormServerErrors<T>(
  errors: ValidationError<T>[],
  setError: (
    fieldName: keyof T,
    error: { type: string; message: string }
  ) => void
) {
  return errors.forEach((error) => {
    error.members.map((m) => {
      setError(m, {
        type: "server",
        message: error.message,
      });
    });
  });
}

export function clip(a: number, min: number, max: number) {
  return Math.min(Math.max(a, min), max);
}
export function getBasePath(locales: readonly string[]) {
  if (!isServer()) {
    const { pathname } = window.location;

    const split = pathname.split("/");
    //distract level 2 path
    const locale = split[1];

    const base = window.location.origin; //origin with protocol and host
    //check if includes
    if (locales.includes(locale)) {
      return `${base}/${locale}`;
    } else {
      return base;
    }
  }
}

export function dataURItoBlob(dataURI: string | null) {
  if (!dataURI) return null;
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(",")[0].indexOf("base64") >= 0)
    byteString = atob(dataURI.split(",")[1]);
  else byteString = unescape(dataURI.split(",")[1]);

  // separate out the mime component
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], { type: mimeString });
}

export function commafy(number: string, delimiterChar = ",") {
  if (!number) {
    return number;
  }
  const str = number.split(delimiterChar).join("").split(".");

  if (str[0].length >= 4) {
    str[0] = str[0].replace(/(\d)(?=(\d{3})+$)/g, "$1" + delimiterChar);
  }
  if (str[1] && str[1].length >= 4) {
    str[1] = str[1].replace(/(\d{3})/g, "$1 ");
  }
  return str.join(".");
}

export const useIsMount = () => {
  const isMountRef = useRef(true);
  useEffect(() => {
    isMountRef.current = false;
  }, []);
  return isMountRef.current;
};

export const setRoute = (object: { [key: string]: any }, path: string) => {
  let paramsQuery = {};
  let stringParams = "";
  Object.entries(object).map((d) => {
    if (d[1] !== undefined) {
      paramsQuery = { ...paramsQuery, [d[0]]: d[1] };
      if (!stringParams.length) {
        stringParams = `?${d[0]}=${d[1]}`;
      } else {
        stringParams = `${stringParams}&${d[0]}=${d[1]}`;
      }
    }
  });
  Router.push(decodeURIComponent(`${path}${stringParams}`), undefined, {
    shallow: true,
  });
  return paramsQuery;
};

export const cleanBackToBackSpaces = (str: string) => {
  return str.replace(/\s+/g, " ");
};

export const formattedNumber = (title: number, isMobile: boolean = false) =>
  title >= (isMobile ? 1000000 : 1_000_000_000)
    ? separateMoneyNumbers(toFarsiNumber(isMobile ? 999999 : 999_999_999)) + "+"
    : separateMoneyNumbers(toFarsiNumber(title));

export const isThisStringHtmlCode = (input: string): boolean => {
  const htmlPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlPattern.test(input);
};

export const isInDevEnv = (includeTestBranchs?: boolean) => {
  const isDevEnv = process.env.NODE_ENV === "development";
  if (isDevEnv) return true;
  else {
    const hostname =
      typeof window !== "undefined" ? window.location.hostname : "";
    const isDevDomain = hostname.includes("localhost");
    const isInTestDomain = hostname.includes("front.frdrs.ir");
    return includeTestBranchs
      ? Boolean(isDevDomain || isInTestDomain)
      : isDevDomain;
  }
};

export const convertSecondsToTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds}`;
};

export const customPaginater = (arr: any[], pageSize: number) => {
  if (!Array.isArray(arr) || !pageSize) return [];
  const lcm = (a: number, b: number) => (a * b) / gcd(a, b);
  const gcd = (a: number, b: number): any => (b === 0 ? a : gcd(b, a % b));

  const totalLength = lcm(arr.length, pageSize);
  const repeatCount = totalLength / arr.length;

  const repeated = Array.from({ length: repeatCount }, () => arr).flat();

  const pages = [];
  for (let i = 0; i < repeated.length; i += pageSize) {
    pages.push(repeated.slice(i, i + pageSize));
  }

  return pages;
};

export const CODE_LINE_TAGS = [
  "address",
  "article",
  "blockquote",
  "dd",
  "div",
  "dl",
  "dt",
  "figcaption",
  "figure",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "li",
  "ol",
  "p",
  "pre",
  "section",
  "tr",
  "ul",
];
